import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  Flex,
  Text,
} from '@backstage/ui';
import type { TrackDefinition } from '@internal/backstage-plugin-pulse-common';
import type { TrackRecertificationSummary } from '../../hooks';

export type TrackRecertifyDialogProps = {
  track: TrackDefinition;
  isOpen: boolean;
  onClose: () => void;
  recertify: (trackId: string) => Promise<TrackRecertificationSummary>;
  recertifying: boolean;
};

/** Confirmation dialog for recertifying a track from its stored check results. */
export const TrackRecertifyDialog = ({
  track,
  isOpen,
  onClose,
  recertify,
  recertifying,
}: TrackRecertifyDialogProps) => {
  const [recertifyError, setRecertifyError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);

  const handleClose = () => {
    setRecertifyError(null);
    setSummary(null);
    onClose();
  };

  const handleRecertify = async () => {
    setRecertifyError(null);
    try {
      const result = await recertify(track.id);
      setSummary(
        `${result.processed} processed, ${result.certified} certified, ${result.failed} failed`,
      );
    } catch (error_) {
      setRecertifyError((error_ as Error).message);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={open => {
        if (!open) {
          handleClose();
        }
      }}
      width={480}
    >
      <DialogHeader>Recertify track</DialogHeader>
      <DialogBody>
        <Flex direction="column" gap="2">
          <Text variant="body-medium" color="secondary">
            {`Recertify "${track.name}" using its latest stored check results. This does not refresh checks or collect facts.`}
          </Text>
          {summary ? <Text variant="body-small">{summary}</Text> : null}
          {recertifyError ? (
            <Text variant="body-small" color="danger">
              {recertifyError}
            </Text>
          ) : null}
        </Flex>
      </DialogBody>
      <DialogFooter>
        <Flex justify="end" gap="2">
          <Button
            variant="secondary"
            isDisabled={recertifying}
            onClick={handleClose}
          >
            Close
          </Button>
          <Button
            variant="primary"
            isPending={recertifying}
            isDisabled={recertifying || Boolean(summary)}
            onClick={handleRecertify}
          >
            Recertify
          </Button>
        </Flex>
      </DialogFooter>
    </Dialog>
  );
};
